import React, { useEffect, useMemo, useState } from 'react'
import { Layout } from '@components/common'
import { getConfig } from '@framework/api'
import getSiteInfo from '@framework/common/get-site-info'
import type { GetStaticPropsContext } from 'next'
import { getPageData } from '@utils/contentful/get-page-data'
import { revalidatePeriod } from '@utils/server-side-config'
import { GenericContentBlock } from 'types/contentful/genericContentBlock'
import { mapContentfulImageAsset } from '@utils/contentful/contentfulImage'
import {
  UploadDropzone,
  UploaderState,
} from '@pageFeatures/design/designUpload/components/UploadDropzone/UploadDropzone'
import { ContentfulMarkdown } from '@utils/contentful/ContentfulMarkdown'
import { VariantOptionSelection } from '@pageFeatures/design/designUpload/components/VariantOptionSelection'
import isEmpty from 'lodash/isEmpty'
import sortBy from 'lodash/sortBy'
import {
  EditorContentfulConfiguration,
  GuidelineConfigurationWithAsset,
  ProductVariant,
  TOnlineEditorConfiguration,
} from '@pageFeatures/design/designUpload/types'

import parentLogger from '@utils/logger'
import useProductOptionSelection from '@pageFeatures/design/designUpload/hooks/useProductOptionSelection/useProductOptionSelection'
import { hydrateGuidelineDownloadConfigurationWithAssetData } from '@pageFeatures/design/designUpload/utils/uploadPage.utils'
import { SummaryStickyFooter } from '@components/common/SummaryStickyFooter'
import { useRouter } from 'next/router'
import { formatPrice } from '@commerce/product/use-price'
import {
  NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY,
  NEXT_PUBLIC_TAX_TYPE,
} from '@utils/client-side-config'
import loadEditorConfig from '@pageFeatures/design/designUpload/contentful/loadEditorConfig'
import { GuidelineDownload } from '@pageFeatures/design/designUpload/components/GuidelineDownload'
import useUploaderToCart from '@pageFeatures/design/designUpload/hooks/useUploaderToCart/useUploaderToCart'
import { useUI } from '@components/ui'
import usePreloadEditorConfiguration from '@pageFeatures/design/designUpload/hooks/usePreloadEditorConfiguration'
import useGuidelines from '@pageFeatures/design/designUpload/hooks/useGuidelines'
import { ContentfulPage } from 'app/_components/ContentfulPage/ContentfulPage'
import { getContentfulComponents } from '@utils/contentful/contentfulComponents/pageContent'
import { TContentfulComponents } from 'types/contentful/contentfulComponents'
import useCart from '@framework/cart/use-cart'
import { getProductsWithPrice } from '@utils/platform/products'
import { startRenderTransaction, finishRenderTransaction } from '@utils/tracing'
import {
  RadioTileGroup,
  IRadioTileOption,
} from '@noissue-ui-kit/oldBranding/radioTile'
import { DESIGN_PATHS } from '@pageFeatures/design/constants'
import { BriefingForm } from '@pageFeatures/design/briefingForm/components/BriefingForm'
import { parseBriefConfiguration } from '@pageFeatures/design/briefingForm/utils/parseBriefConfiguration'
import { IBriefStep } from '@pageFeatures/design/briefingForm/types'
import { BriefingFormDataProvider } from '@pageFeatures/design/briefingForm/components/BriefingFormDataProvider'
import {
  getDesignerChatbotConfigs,
  TDesignChatBotUseCases,
} from '@pageFeatures/design/briefingForm/utils/parseDesignerChatbotConfiguration'
import {
  Accordion,
  AccordionItem,
  IAccordionItemConfig,
} from '@noissue-ui-kit/oldBranding/accordion'
import truncate from 'lodash.truncate'
import { Modal } from '@noissue-ui-kit/modal'
import { briefingFormWasUpdated } from '@pageFeatures/design/briefingForm/utils'
import {
  trackDesignChoice,
  trackDesignerFileUpload,
  trackDesignerInteraction,
} from '@utils/gtm'
import { TDesignJourney, TOnlineEditorState } from '@pageFeatures/design/types'
import {
  LegacyV3EditorAlertMessage,
  checkIfLegacyEditor,
} from '@pageFeatures/design/legacyEditors.utils'

import { EditorWrapper } from '@pageFeatures/design/onlineEditor/EditorWrapper'
import { editorProductsConfig } from '@pageFeatures/design/onlineEditor/editor-products.config'
import { UnifiedEditorProvider } from '@pageFeatures/design/UnifiedEditorProvider'
import { IPdfGenerationPayload } from '@pageFeatures/design/onlineEditor/editor.types'
import { getOnlineEditorCustomizationObject } from '@pageFeatures/design/onlineEditor/editor.utils'
import {
  DesignerChatbot,
  IDesignerChatbot,
} from '@pageFeatures/design/DesignerChatbot'
import { legacyDesignJourneyToNew } from '@pageFeatures/design/utils'
import { BriefButtonTileSelection } from '@pageFeatures/design/briefingForm/components/entryFields/BriefButtonTileSelection'
import { UploadDropzoneProvider } from '@pageFeatures/design/designUpload/components/UploadDropzone/UploadDropzone.provider'
import { UPLOADER_FALLBACK_COPY } from '@pageFeatures/design/designUpload/components/UploadDropzone/UploadDropzone.utils'
import { FormCheckbox } from 'noissueUiKit/form/formCheckbox'
import { Transition } from '@headlessui/react'
import * as Sentry from '@sentry/nextjs'

import {
  Breadcrumbs,
  BreadcrumbsItem,
} from '@noissue-ui-kit/oldBranding/breadcrumbs'
import {
  breadcrumbsLinksToStructuredData,
  getPageBreadcrumbs,
} from '@utils/breadcrumbs'
import { IBreadcrumbLink } from 'types/breadcrumbs'
import { BreadcrumbJsonLd } from 'next-seo'
import { RadioTileWithImage } from '@components/common/RadioTileWithImage'

const logger = parentLogger.child({ name: 'upload-[slug].tsx' })

const MAX_STEP_LABEL_LENGTH = 65
const QUANTITY_SELECTOR_NAME = 'quantity'

export async function getStaticProps({
  params,
  preview,
  locale,
}: GetStaticPropsContext<{ slug: string }>) {
  const tx = startRenderTransaction('/upload/[slug]', params.slug)

  const config = getConfig({ locale })
  const { categories } = await getSiteInfo({ config, preview })
  const pageData = await getPageData({ categories, preview })
  const editorConfiguration = await loadEditorConfig(params?.slug, preview)

  if (isEmpty(editorConfiguration)) {
    return {
      notFound: true,
    }
  }

  const productVariantsSales = await getProductsWithPrice(
    editorConfiguration?.productSlug
  )

  if (isEmpty(productVariantsSales)) {
    logger.error('The product unavailable in the area')
    return {
      redirect: {
        destination: `/custom-packaging/${params?.slug}`,
      },
    }
  }

  if (isEmpty(editorConfiguration)) {
    logger.error(`Editor configuration not found`)

    return {
      notFound: true,
    }
  }

  const guidelineConfigurationWithLinkedAssets =
    await hydrateGuidelineDownloadConfigurationWithAssetData(
      editorConfiguration?.guidelineConfiguration
    )

  const { slotAComponentProps: contentfulPageComponents } =
    await getContentfulComponents({
      pageSlug: '/upload/' + params?.slug,
      dataForParsers: {
        preview,
        categories,
      },
    })

  finishRenderTransaction(tx)

  return {
    props: {
      ...pageData,
      contentfulPageComponents: contentfulPageComponents,
      editorConfiguration,
      productVariantsSales: sortBy(productVariantsSales, 'price'), // All products with the prices. So if product has zero price, it won't show
      hideLayoutFooter: true,
      guidelineConfigurationWithLinkedAssets,
    },
    revalidate: revalidatePeriod(), // In seconds
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export function toUploadContent(
  sectionContent: GenericContentBlock,
  fallbackCopy: { title: string; subtitle: string }
) {
  return {
    title: sectionContent?.h1 || fallbackCopy?.title,
    subtitle: sectionContent?.h2 || fallbackCopy?.subtitle,
    footer: sectionContent?.h3,
    icon: mapContentfulImageAsset(sectionContent?.primaryImages?.[0]),
  }
}

export default function Slug({
  editorConfiguration,
  productVariantsSales,
  guidelineConfigurationWithLinkedAssets,
  contentfulPageComponents,
}: {
  productCategorySlug: string
  editorConfiguration: EditorContentfulConfiguration
  productVariantsSales: ProductVariant[]
  guidelineConfigurationWithLinkedAssets: GuidelineConfigurationWithAsset
  contentfulPageComponents: TContentfulComponents
  productTemplates: any
}) {
  const router = useRouter()
  const [selectedDesignJourney, setSelectedDesignJourney] =
    useState<TDesignJourney>(DESIGN_PATHS.DESIGN_UPLOAD)
  const [designPathsOptions, setDesignPathsOptions] = useState<
    IRadioTileOption[]
  >(() => {
    return [
      {
        key: DESIGN_PATHS.DESIGN_UPLOAD,
        displayLabel: 'Upload artwork',
      },
    ]
  })
  const [briefingFormOutput, setBriefingFormOutput] = useState<
    Record<string, string | never>
  >({})
  const {
    urlSlug,
    productName,
    productSlug,
    instructions,
    uploaderIdleStateContent,
    uploaderDragOverStateContent,
    uploaderSuccessStateContent,
    briefConfiguration,
    onlineEditorIsProductionReady,
    qrCodeQuestionStep,
    enableSkipArtworkDesignJourney,
    breadcrumbs: breadcrumbsConfig,
  } = editorConfiguration

  const onlineEditorConfig = editorProductsConfig[urlSlug]
  const displayDesignPathChoice =
    !isEmpty(briefConfiguration) || !isEmpty(onlineEditorConfig)

  const parsedBriefConfiguration: IBriefStep[] =
    parseBriefConfiguration(briefConfiguration)

  const parsedQrCodeStep: IBriefStep = parseBriefConfiguration([
    qrCodeQuestionStep,
  ])?.[0]

  const designerChatbotConfigs: Record<
    TDesignChatBotUseCases,
    IDesignerChatbot
  > = getDesignerChatbotConfigs(editorConfiguration)

  const { preloadConfiguration, configurationIsLoading } =
    usePreloadEditorConfiguration({
      fieldsVisibilityConfiguration: editorConfiguration.fieldsConfiguration,
      query: router?.query,
      productVariantsSales,
    })

  const {
    state: optionSelectionState,
    selectorsConfiguration,
    select,
    productOptionSelected,
    upload,
    removeUploadFile,
    isDesignReadyForAddToCart,
    getFileUploadedUrls,
    editingItemId,
    setPreloadConfiguration,
    getQuantityOptions,
    clearUploadedFiles,
  } = useProductOptionSelection({
    productCategorySlug: productSlug,
    productVariantsSales,
    editorConfiguration,
  })

  const [addItemToCart] = useUploaderToCart()
  const { openSidebar } = useUI()
  const { data: cartContent } = useCart()
  const [settingsStepLabel, setSettingStepLabel] = useState<string>(null)
  const [quantityStepLabel, setQuantityStepLabel] = useState<string>(null)
  const [forcedAccordionState, setForcedAccordionState] =
    useState<IAccordionItemConfig[]>(null)
  const [accordionState, setAccordionState] =
    useState<IAccordionItemConfig[]>(null)
  const designIsUploaded = isDesignReadyForAddToCart()
  const readOnlyMode = router?.query?.readOnly === 'true'
  const [preloadedBriefConfig, setPreloadedBriefConfig] = useState(null)
  const [
    needDesignJourneyChangeConfirmation,
    setNeedDesignJourneyChangeConfirmation,
  ] = useState<boolean>(false)
  const [pendingDesignJourneyChoice, setPendingDesignJourneyChoice] =
    useState<IRadioTileOption>(null)
  const [pdfGenerationPayload, setPdfGenerationPayload] =
    useState<IPdfGenerationPayload>(null)
  const [onlineEditorOrderPayload, setOnlineEditorOrderPayload] =
    useState<TOnlineEditorConfiguration>(null)
  const [preloadedOnlineEditorConfig, setPreloadedOnlineEditorConfig] =
    useState<TOnlineEditorConfiguration>(null)
  const [purchaseInProgress, setPurchaseInProgress] = useState<boolean>(false)
  const [addToCartError, setAddToCartError] = useState<string>(null)
  const [editorState, setEditorState] = useState<TOnlineEditorState>('initial')
  const [qrCodeChoice, setQrCodeChoice] = useState<string>(null)

  useEffect(() => {
    if (isEmpty(preloadConfiguration)) {
      return
    }

    setPreloadConfiguration(preloadConfiguration)
  }, [preloadConfiguration])

  const selectors = selectorsConfiguration()
  const productOption = productOptionSelected()

  const { guidelineAssets, selectedVariantOptions } = useGuidelines({
    guidelineConfiguration: guidelineConfigurationWithLinkedAssets,
    selectedVariant: productOption,
  })

  const scrollToTheTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const contentfulComponentCustomCTAs = {
    scrollPageToTheTop: scrollToTheTop,
  }

  useEffect(() => {
    if (!isEmpty(preloadConfiguration?.brief)) {
      setPreloadedBriefConfig(preloadConfiguration.brief)
    }

    if (!isEmpty(preloadConfiguration?.onlineEditor)) {
      setPreloadedOnlineEditorConfig(preloadConfiguration?.onlineEditor)
    }

    let legacyOptionSuccessorIsAvailable = true
    const convertedLegacyJourney =
      legacyDesignJourneyToNew[preloadConfiguration?.version]

    if (convertedLegacyJourney === 'editor') {
      legacyOptionSuccessorIsAvailable = Boolean(
        editorProductsConfig[productSlug]
      )
    }

    const preloadedDesignJourney =
      preloadConfiguration?.designJourney ||
      (legacyOptionSuccessorIsAvailable
        ? legacyDesignJourneyToNew[preloadConfiguration?.version]
        : null)

    if (preloadedDesignJourney) {
      setSelectedDesignJourney(preloadedDesignJourney)
    }
  }, [preloadConfiguration])

  useEffect(() => {
    const productSettingsSelectors = selectors.filter(
      (selector) => selector.name !== 'quantity'
    )
    const settingsLabel = productSettingsSelectors?.reduce(
      (label, currentSelector) => {
        const optionValue = productOption?.[currentSelector?.name]
        return (label ? `${label}, ` : '') + (optionValue || '')
      },
      ''
    )

    setSettingStepLabel('Product Settings: ' + settingsLabel)
    setQuantityStepLabel(
      productOption?.quantity
        ? `Product Quantity: ${productOption?.quantity}`
        : ''
    )
  }, [productOption])

  useEffect(() => {
    if (router?.query?.configId) {
      setForcedAccordionState([
        { opened: true, confirmed: true },
        { opened: readOnlyMode, confirmed: true },
        { opened: readOnlyMode, confirmed: true },
        { opened: readOnlyMode, confirmed: true },
      ])
    } else {
      setPreloadedBriefConfig(null)
    }
  }, [readOnlyMode, router?.query?.configId])

  useEffect(() => {
    if (
      briefingFormOutput &&
      briefingFormWasUpdated({
        briefingFormOutput,
      })
    ) {
      setNeedDesignJourneyChangeConfirmation(true)
    }
  }, [briefingFormOutput])

  const clearSelectedDesignJourneyData = (designPath) => {
    switch (designPath.key) {
      case 'briefing-form':
        setBriefingFormOutput(null)
        break
      default:
        break
    }
  }

  const trackAccordionInteraction = ({ index, action }) => {
    const stepsNames = ['Product Settings', 'Quantity', 'Briefing form']
    trackDesignerInteraction({
      event: 'designer-interaction',
      designerName: selectedDesignJourney,
      componentName:
        action === 'confirmed' ? 'ConfirmationButton' : 'AccrodionStepHeader',
      componentInformation: `${stepsNames[index]} step was ${action}`,
      selectedProductSlug: router.query?.slug.toString(),
      displayType: 'AccordionStep',
    })
  }

  const trackUploadedFile = ({ fileUrl }) => {
    trackDesignerFileUpload({
      fileUrl,
      designJourney: selectedDesignJourney as TDesignJourney,
      productSlug,
    })
  }

  useEffect(() => {
    if (selectedDesignJourney) {
      trackDesignChoice({
        selectedOptionLabel: selectedDesignJourney,
        selectedProductSlug: productSlug,
      })
    } else {
      setNeedDesignJourneyChangeConfirmation(false)
    }
    setEditorState('initial')
  }, [selectedDesignJourney])

  useEffect(() => {
    if (
      !isEmpty(onlineEditorConfig) &&
      (process.env.NEXT_PUBLIC_REQUIRE_APPROVAL_FOR_ONLINE_EDITOR === 'true'
        ? onlineEditorIsProductionReady
        : true) &&
      !designPathsOptions.find((x) => x.key === DESIGN_PATHS.ONLINE_EDITOR)
    ) {
      setDesignPathsOptions((prevState) => {
        return [
          ...prevState,
          {
            key: DESIGN_PATHS.ONLINE_EDITOR,
            displayLabel: onlineEditorConfig?.enabledWidgets?.logo
              ? 'Customize artwork'
              : 'Create pattern',
          },
        ]
      })
    }

    if (
      !isEmpty(briefConfiguration) &&
      !designPathsOptions.find((x) => x.key === DESIGN_PATHS.BRIEFING_FORM)
    ) {
      setDesignPathsOptions((prevState) => {
        return [
          ...prevState,
          {
            key: DESIGN_PATHS.BRIEFING_FORM,
            displayLabel: 'Brief a designer',
          },
        ]
      })
    }
  }, [briefConfiguration, onlineEditorConfig, onlineEditorIsProductionReady])

  const canPurchase =
    ((designIsUploaded &&
      selectedDesignJourney !== DESIGN_PATHS.SKIP_ARTWORK) ||
      selectedDesignJourney === DESIGN_PATHS.SKIP_ARTWORK) &&
    editorState !== 'error' &&
    editorState !== 'loading'

  const breadcrumbsLinks: IBreadcrumbLink[] = useMemo(() => {
    return getPageBreadcrumbs([
      ...(breadcrumbsConfig?.map((x) => ({
        name: x.fields.ctaLabel,
        path: x.fields.ctaLink,
      })) || []),
      {
        path: router?.asPath,
        name: 'Design',
      },
    ])
  }, [breadcrumbsConfig])

  const qrCodeChoiceDefaultOption =
    parsedQrCodeStep?.options?.find(
      (x) => x.value === preloadConfiguration?.qrCodeChoice
    ) || parsedQrCodeStep?.options?.[parsedQrCodeStep?.options?.length - 1]

  return (
    <>
      <UploadDropzoneProvider
        initialFileUrls={preloadConfiguration.filesUrls}
        forceReset={optionSelectionState?.forceResetUploadDropzone}
      >
        <UnifiedEditorProvider
          selectedDesignJourney={selectedDesignJourney}
          designerChatbotConfigs={designerChatbotConfigs}
          editorState={editorState}
          setEditorState={setEditorState}
        >
          {breadcrumbsLinks?.length > 0 && (
            <Breadcrumbs className="mx-auto py-6 h-20 max-w-screen-xl px-5 sm:px-16">
              {breadcrumbsLinks.map((breadcrumb, index) => (
                <BreadcrumbsItem
                  key={`breadcrumbsItem-${index}`}
                  href={breadcrumb.url}
                  text={breadcrumb.name}
                />
              ))}
            </Breadcrumbs>
          )}

          <BreadcrumbJsonLd
            itemListElements={breadcrumbsLinksToStructuredData(
              breadcrumbsLinks
            )}
          />

          {(configurationIsLoading || !router?.isReady) && (
            <div className="fixed top-0 bottom-0 left-0 right-0 z-20 flex items-center justify-center bg-white opacity-80">
              <img
                className="w-40 h-40 -translate-y-1/2 opacity-100 top-1/2 left-1/2"
                src={'/icon-uploading-new-brand.gif'}
                alt="uploading"
              />
            </div>
          )}

          {router?.isReady && !configurationIsLoading && (
            <div className="lg:overflow-visible">
              <div className="flex flex-wrap w-full max-w-screen-xl px-4 m-auto overflow-visible md:px-16 lg:flex-nowrap">
                <div className="w-full lg:mr-6 lg:mb-36 md:w-1/2">
                  {checkIfLegacyEditor(preloadConfiguration) && (
                    <LegacyV3EditorAlertMessage />
                  )}
                  <div className="flex-1 w-full p-0 mr-6 md:mb-36 sm:p-16 sm:bg-core-grey-10 rounded-3xl">
                    <h1 className="mb-4 text-4xl font-semibold text-acai font-mori">
                      {productName}
                    </h1>

                    <ContentfulMarkdown overrideClass="font-mori text-boulder">
                      {instructions?.fields?.h1}
                    </ContentfulMarkdown>

                    <Accordion
                      initialMode={
                        router?.query?.configId ? 'hideAll' : 'showFirst'
                      }
                      className="my-10"
                      initialState={forcedAccordionState}
                      onChange={setAccordionState}
                      requireConfirmation={true}
                      trackInteraction={trackAccordionInteraction}
                    >
                      <AccordionItem
                        label={
                          accordionState?.[0]?.confirmed
                            ? truncate(settingsStepLabel, {
                                length: MAX_STEP_LABEL_LENGTH,
                              })
                            : `Step 1: Select your product settings`
                        }
                        labelClassName="text-acai text-2xl font-bold"
                        disableConfirmation={readOnlyMode}
                      >
                        <div className="mt-4">
                          {selectors
                            ?.map((option) => {
                              if (
                                (option.values.length === 1 &&
                                  !option.forceShow) ||
                                option.name === QUANTITY_SELECTOR_NAME
                              ) {
                                return null
                              }

                              const selectedValue = productOption?.[option.name]
                              return (
                                <VariantOptionSelection
                                  selectChange={select}
                                  selectedValue={
                                    selectedValue
                                      ? {
                                          key: String(selectedValue),
                                          displayLabel: String(selectedValue),
                                        }
                                      : undefined
                                  }
                                  key={option.name}
                                  option={option}
                                  getQuantityOptions={getQuantityOptions}
                                  readOnlyMode={readOnlyMode}
                                />
                              )
                            })
                            .filter(Boolean)}
                        </div>
                      </AccordionItem>

                      <AccordionItem
                        label={
                          accordionState?.[1].confirmed
                            ? truncate(quantityStepLabel, {
                                length: MAX_STEP_LABEL_LENGTH,
                              })
                            : `Step 2: Choose your quantity`
                        }
                        labelClassName="text-acai text-2xl font-bold"
                        disableConfirmation={readOnlyMode}
                      >
                        <div className="mt-4">
                          {[
                            selectors?.find(
                              (option) =>
                                option.name === QUANTITY_SELECTOR_NAME &&
                                (option.values.length > 1 || option.forceShow)
                            ),
                          ].map((option) => {
                            return (
                              option && (
                                <VariantOptionSelection
                                  selectChange={select}
                                  selectedValue={
                                    productOption?.quantity
                                      ? {
                                          key: String(productOption?.quantity),
                                          displayLabel: String(
                                            productOption?.quantity
                                          ),
                                        }
                                      : undefined
                                  }
                                  key={option.name}
                                  option={option}
                                  getQuantityOptions={getQuantityOptions}
                                  showLabel={false}
                                  readOnlyMode={readOnlyMode}
                                />
                              )
                            )
                          })}
                        </div>
                      </AccordionItem>

                      {!isEmpty(parsedQrCodeStep) && (
                        <AccordionItem
                          label="Step 3: Add packaging-alliance certification"
                          labelClassName="text-acai text-2xl font-bold"
                          disableConfirmation={readOnlyMode}
                          contentContainerClassName="pt-10 mb-10"
                        >
                          <p className="mb-5 text-2xl font-semibold text-acai">
                            {parsedQrCodeStep.label}
                          </p>

                          <RadioTileWithImage
                            options={parsedQrCodeStep.options}
                            onChange={(event) => {
                              setQrCodeChoice(event.value)
                            }}
                            defaultOption={qrCodeChoiceDefaultOption}
                          />
                        </AccordionItem>
                      )}

                      {displayDesignPathChoice ? (
                        <AccordionItem
                          label={`Step ${
                            isEmpty(parsedQrCodeStep) ? '3' : '4'
                          }: Choose how to design your packaging`}
                          labelClassName="text-acai text-2xl font-bold"
                          contentContainerClassName="pb-96"
                          disableConfirmation={readOnlyMode}
                        >
                          {enableSkipArtworkDesignJourney && (
                            <FormCheckbox
                              name="skipArtworkOption"
                              label="Skip this step, and email artwork later"
                              description={
                                editorConfiguration?.skipArtworkOptionInstructions
                              }
                              className="mt-8"
                              colour="pitaya"
                              showDescription={
                                selectedDesignJourney ===
                                DESIGN_PATHS.SKIP_ARTWORK
                              }
                              initialValue={
                                selectedDesignJourney ===
                                DESIGN_PATHS.SKIP_ARTWORK
                              }
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setSelectedDesignJourney(
                                    DESIGN_PATHS.SKIP_ARTWORK
                                  )
                                } else {
                                  setSelectedDesignJourney(
                                    DESIGN_PATHS.DESIGN_UPLOAD
                                  )
                                }
                              }}
                            />
                          )}

                          <div className="mt-4">
                            {!readOnlyMode && (
                              <RadioTileGroup
                                options={designPathsOptions}
                                optionName="Design"
                                onChange={(selectedOption) => {
                                  if (needDesignJourneyChangeConfirmation) {
                                    setPendingDesignJourneyChoice(
                                      selectedOption
                                    )
                                  } else {
                                    setSelectedDesignJourney(
                                      selectedOption.key as TDesignJourney
                                    )
                                  }
                                }}
                                selected={{
                                  key: selectedDesignJourney,
                                  displayLabel: designPathsOptions.find(
                                    (path) => path.key === selectedDesignJourney
                                  )?.displayLabel,
                                }}
                                radioGroupClassName={`relative mb-9 z-0 ${
                                  designPathsOptions.length % 2 === 0
                                    ? 'grid-cols-1 md:grid-cols-2'
                                    : 'grid-cols-1 md:grid-cols-3'
                                }`}
                              />
                            )}

                            <Transition
                              show={
                                !readOnlyMode &&
                                selectedDesignJourney !==
                                  DESIGN_PATHS.SKIP_ARTWORK
                              }
                              enter="transition-all duration-300 ease-linear"
                              enterFrom="opacity-0 max-h-0"
                              enterTo="opacity-100 max-h-72"
                              entered="transition-all duration-300 ease-linear"
                              leave="transition-all duration-300 ease-linear"
                              leaveFrom="opacity-100 max-h-72"
                              leaveTo="opacity-0 max-h-0"
                            >
                              <DesignerChatbot />
                            </Transition>

                            {selectedDesignJourney ===
                              DESIGN_PATHS.DESIGN_UPLOAD && (
                              <>
                                <GuidelineDownload
                                  guidelineAssets={guidelineAssets}
                                  selectedVariantOptions={
                                    selectedVariantOptions
                                  }
                                  className="mb-12"
                                />
                              </>
                            )}

                            {selectedDesignJourney ===
                              DESIGN_PATHS.BRIEFING_FORM && (
                              <BriefingFormDataProvider
                                value={{
                                  briefingFormOutput,
                                  setBriefingFormOutput,
                                  productOption,
                                  preloadedAnswers:
                                    preloadedBriefConfig || null,
                                  readOnlyMode,
                                }}
                              >
                                <BriefingForm
                                  productSlug={productSlug}
                                  contentConfig={parsedBriefConfiguration}
                                />
                                <p className="text-md text-black-60">
                                  Our designers will prepare your proof with the
                                  artwork&nbsp;<strong>you provide</strong>,
                                  ensuring print-perfection and satisfaction.
                                </p>
                              </BriefingFormDataProvider>
                            )}

                            {selectedDesignJourney ===
                              DESIGN_PATHS.ONLINE_EDITOR && (
                              <UploadDropzone
                                idleStateContent={toUploadContent(
                                  {
                                    ...uploaderIdleStateContent?.fields,
                                    h3: 'We prefer SVG, EPS, PDF (JPG/PNG are also accepted). Max file size: 100MB',
                                  } as GenericContentBlock,
                                  UPLOADER_FALLBACK_COPY[UploaderState.IDLE]
                                )}
                                successStateContent={toUploadContent(
                                  uploaderSuccessStateContent?.fields,
                                  UPLOADER_FALLBACK_COPY[UploaderState.SUCCESS]
                                )}
                                hoverStateContent={toUploadContent(
                                  uploaderDragOverStateContent?.fields,
                                  UPLOADER_FALLBACK_COPY[UploaderState.HOVER]
                                )}
                                upload={upload}
                                removeUploadFile={removeUploadFile}
                                className={
                                  readOnlyMode
                                    ? 'pointer-events-none'
                                    : 'pointer-events-auto'
                                }
                                trackUploadedFile={trackUploadedFile}
                                selectable={true}
                                preselectedFileName={
                                  preloadedOnlineEditorConfig?.selectedFileName
                                }
                                uploadType="svg-convert"
                              />
                            )}
                          </div>
                        </AccordionItem>
                      ) : (
                        <AccordionItem
                          label={`Step ${
                            isEmpty(parsedQrCodeStep) ? '3' : '4'
                          }:  Download guidelines and upload artwork`}
                          labelClassName="text-acai text-2xl font-bold"
                          disableConfirmation={
                            router?.query?.readOnly === 'true'
                          }
                        >
                          <div className="mt-4">
                            <GuidelineDownload
                              guidelineAssets={guidelineAssets}
                              selectedVariantOptions={selectedVariantOptions}
                              className="mb-12"
                            />
                          </div>
                        </AccordionItem>
                      )}
                    </Accordion>
                  </div>
                </div>

                <div className="w-full lg:w-auto md:flex-1">
                  {selectedDesignJourney === DESIGN_PATHS.ONLINE_EDITOR && (
                    <EditorWrapper
                      productOption={productOption}
                      urlSlug={urlSlug}
                      onDesignConfigChange={({
                        pdfGenerationPayload,
                        onlineEditorOrderPayload,
                      }) => {
                        setPdfGenerationPayload(pdfGenerationPayload)
                        setOnlineEditorOrderPayload(onlineEditorOrderPayload)
                      }}
                      preloadedOnlineEditorConfig={preloadedOnlineEditorConfig}
                      readOnlyMode={readOnlyMode}
                      announceDirtyForm={setNeedDesignJourneyChangeConfirmation}
                    />
                  )}

                  {selectedDesignJourney !== DESIGN_PATHS.ONLINE_EDITOR && (
                    <div className="sticky top-60 mb-36">
                      <UploadDropzone
                        idleStateContent={toUploadContent(
                          uploaderIdleStateContent?.fields,
                          UPLOADER_FALLBACK_COPY[UploaderState.IDLE]
                        )}
                        successStateContent={toUploadContent(
                          uploaderSuccessStateContent?.fields,
                          UPLOADER_FALLBACK_COPY[UploaderState.SUCCESS]
                        )}
                        hoverStateContent={toUploadContent(
                          uploaderDragOverStateContent?.fields,
                          UPLOADER_FALLBACK_COPY[UploaderState.HOVER]
                        )}
                        upload={upload}
                        removeUploadFile={removeUploadFile}
                        className={`min-h-[450px] mb-36 ${
                          readOnlyMode
                            ? 'pointer-events-none'
                            : 'pointer-events-auto'
                        }`}
                        trackUploadedFile={trackUploadedFile}
                        selectable={false}
                        disableUpload={
                          readOnlyMode ||
                          selectedDesignJourney === DESIGN_PATHS.SKIP_ARTWORK
                        }
                        uploadType="upload"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {contentfulPageComponents?.length ? (
            <ContentfulPage
              componentsProps={contentfulPageComponents}
              customCTAsMapper={contentfulComponentCustomCTAs}
            />
          ) : null}

          <Modal
            opened={!!pendingDesignJourneyChoice}
            size="small"
            title="Are you sure you want to switch design path?"
            subtitle="Any progress or input with this design path will be lost if you switch mode now."
            confirmLabel="Yes, continue"
            onConfirm={() => {
              clearSelectedDesignJourneyData(selectedDesignJourney)
              setSelectedDesignJourney(
                pendingDesignJourneyChoice.key as TDesignJourney
              )
              setPendingDesignJourneyChoice(null)
              setNeedDesignJourneyChangeConfirmation(false)
            }}
            onCancel={() => {
              setPendingDesignJourneyChoice(null)
            }}
          />

          <Modal
            opened={purchaseInProgress}
            size="large"
            showControls={{
              confirmButton: false,
              cancelButton: Boolean(addToCartError),
              cancelIcon: Boolean(addToCartError),
            }}
            onCancel={() => setPurchaseInProgress(false)}
          >
            <div className="flex flex-col items-center w-full max-w-lg mx-auto my-36">
              <img
                className="mb-6 w-52 h-52"
                src={
                  addToCartError
                    ? '/images/static-nellie.png'
                    : '/loading-nellie.gif'
                }
                alt="loading icon"
              />
              <h3 className="text-4xl font-semibold text-center">
                {addToCartError || `Hold on. We're packaging up your design...`}
              </h3>
            </div>
          </Modal>

          <SummaryStickyFooter
            price={formatPrice({
              amount: productOption?.price,
              currencyCode:
                productOption?.currencyCode ||
                NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY,
              locale: 'en-US',
            })}
            salePrice={
              productOption?.salePrice &&
              productOption?.salePrice < productOption?.price
                ? formatPrice({
                    amount: productOption?.salePrice,
                    currencyCode:
                      productOption?.currencyCode ||
                      NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY,
                    locale: 'en-US',
                  })
                : undefined
            }
            taxTag={NEXT_PUBLIC_TAX_TYPE ? ` +${NEXT_PUBLIC_TAX_TYPE}` : null}
            leftLink={{
              text: 'Back',
              onClick: () => {
                router.back()
              },
            }}
            variantOptions={[
              {
                key: 'Quantity',
                value: productOption?.quantity,
              },
            ]}
            callToActionButton={{
              text:
                router.query.edit === 'true' ? 'Update Cart' : 'Add to Cart',
              onClick: async () => {
                try {
                  setAddToCartError(null)
                  setPurchaseInProgress(true)
                  let onlineEditorConfig: any = {}

                  if (selectedDesignJourney === DESIGN_PATHS.ONLINE_EDITOR) {
                    onlineEditorConfig =
                      await getOnlineEditorCustomizationObject({
                        onlineEditorOrderPayload,
                        pdfGenerationPayload,
                        parentOrderDesignId:
                          preloadConfiguration?.onlineEditor?.bucketDesignId,
                      })
                  }

                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  await addItemToCart({
                    cartContent,
                    productCategorySlug: urlSlug || productSlug,
                    selection: productOptionSelected(),
                    brief:
                      !isEmpty(briefingFormOutput) &&
                      selectedDesignJourney === DESIGN_PATHS.BRIEFING_FORM
                        ? briefingFormOutput
                        : null,
                    onlineEditor:
                      selectedDesignJourney === DESIGN_PATHS.ONLINE_EDITOR
                        ? onlineEditorConfig
                        : null,
                    filesUrls:
                      selectedDesignJourney === DESIGN_PATHS.SKIP_ARTWORK
                        ? null
                        : getFileUploadedUrls(),
                    itemId: editingItemId(),
                    edit: router.query.edit as string,
                    designJourney: selectedDesignJourney as TDesignJourney,
                    qrCodeChoice,
                  })

                  openSidebar()
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  router.push({ query: { slug: router?.query?.slug } })

                  setForcedAccordionState([
                    { opened: false, confirmed: false },
                    { opened: false, confirmed: false },
                    { opened: false, confirmed: false },
                  ])

                  clearUploadedFiles()
                  setBriefingFormOutput({})
                  setPreloadedBriefConfig(null)
                  setSelectedDesignJourney(DESIGN_PATHS.DESIGN_UPLOAD)
                  setPurchaseInProgress(false)
                } catch (err) {
                  setAddToCartError(
                    'Something went wrong! Please try again later.'
                  )
                  Sentry.captureException(err, {
                    extra: {
                      'noissue.everest': {
                        productSlug,
                        variantSku: productOption.variantSku,
                        variantId: productOption.variantId,
                        productId: productOption.productId,
                        name: productOption.name,
                        modifierId: productOption.modifierId,
                        currencyCode: productOption.currencyCode,
                      },
                    },
                  })
                }
              },
              disabled: !canPurchase || readOnlyMode || purchaseInProgress,

              loading: purchaseInProgress,
            }}
          />
        </UnifiedEditorProvider>
      </UploadDropzoneProvider>
    </>
  )
}

Slug.Layout = Layout
